import React from "react"
import Swal from "sweetalert2"
import moment from "moment"
import { toast } from "react-toastify"
import { usePersonnelDetailsContext } from "../PersonnelDetails"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyGoogleDriveFileViewerInModal from "../../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import {
     personnelDocumentsEmbauchePreviewRequest,
     personnelEnvoyerDocumentsEmbaucheRequest,
     personnelTelechargerDocumentsEmbaucheRequest,
     IPersonnelDocumentsEmbauchePreviewModel,
     IPersonnelDocumentsEmbaucheFileModel,
} from "../../core/_requests"
import { triggerBlobDownload, formatFileSize, MAX_EMAIL_ATTACHMENT_SIZE } from "../../core/_download"

/**
 * Section « Documents d'embauche » de l'onglet Documents d'un collaborateur intégré.
 * Liste les documents du dossier Drive de l'entité de rattachement (le contrat de travail n'y figure
 * pas — il est géré dans « Documents administratifs ») et permet de les envoyer à l'email personnel.
 */
const PersonnelDocumentsEmbauche = () => {
     const context = usePersonnelDetailsContext()
     const p = context.detailsQuery.data?.personnel
     const personnelId = p?.id as number

     const [preview, setPreview] = React.useState<IPersonnelDocumentsEmbauchePreviewModel | null>(null)
     const [loading, setLoading] = React.useState<boolean>(true)
     const [error, setError] = React.useState<string | null>(null)
     const [submitting, setSubmitting] = React.useState<boolean>(false)
     const [downloading, setDownloading] = React.useState<boolean>(false)
     const [activeDoc, setActiveDoc] = React.useState<IPersonnelDocumentsEmbaucheFileModel | null>(null)

     const reload = React.useCallback(() => {
          return personnelDocumentsEmbauchePreviewRequest(personnelId).then(r => setPreview(r.data))
     }, [personnelId])

     React.useEffect(() => {
          setLoading(true)
          personnelDocumentsEmbauchePreviewRequest(personnelId)
               .then(r => setPreview(r.data))
               .catch(e => setError(e?.response?.data?.detail ?? "Impossible de charger les documents d'embauche."))
               .finally(() => setLoading(false))
     }, [personnelId])

     if (loading) return <div className="d-flex justify-content-center py-6"><MySimpleSpinner size={"md"} /></div>
     if (error) return <div className="alert alert-danger mb-0">{error}</div>
     if (!preview) return null

     const suivis = preview.suivis ?? []
     const dejaEnvoye = suivis.length > 0
     const noMail = !preview.mailPersonnel
     const nbDocs = preview.documentsEntite.length
     const maxEmailSize = preview.maxEmailSize ?? MAX_EMAIL_ATTACHMENT_SIZE
     const totalSize = preview.documentsEntite.reduce((n, d) => n + (d.size ?? 0), 0)
     const tooLarge = totalSize > maxEmailSize

     function handleEnvoyer() {
          if (!preview?.mailPersonnel) return
          Swal.fire({
               icon: "question",
               title: "Envoyer les documents d'embauche ?",
               html: `Les documents seront envoyés à <b>${preview.mailPersonnel}</b>.`,
               showCancelButton: true,
               confirmButtonText: "Envoyer",
               cancelButtonText: "Annuler",
               confirmButtonColor: "#50cd89",
          }).then(res => {
               if (!res.isConfirmed) return
               setSubmitting(true)
               personnelEnvoyerDocumentsEmbaucheRequest(personnelId)
                    .then(async () => {
                         toast.success("Les documents d'embauche ont été envoyés.")
                         await reload()
                    })
                    .catch(e => toast.error(e?.response?.data?.detail ?? "L'envoi a échoué.", { autoClose: false }))
                    .finally(() => setSubmitting(false))
          })
     }

     function handleTelecharger() {
          setDownloading(true)
          const label = `${p?.prenom ?? ""}-${p?.nom ?? ""}`.trim() || String(personnelId)
          personnelTelechargerDocumentsEmbaucheRequest(personnelId)
               .then(r => triggerBlobDownload(r.data, `documents-embauche-${label}.zip`))
               .catch(async e => {
                    const detail = e?.response?.data instanceof Blob ? await e.response.data.text().then((t: string) => JSON.parse(t)?.detail).catch(() => null) : e?.response?.data?.detail
                    toast.error(detail ?? "Le téléchargement a échoué.", { autoClose: false })
               })
               .finally(() => setDownloading(false))
     }

     return (
          <div>
               <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-3 mb-4">
                    <div>
                         <div className="fw-bolder fs-5 text-gray-800">Documents du dossier de l'entité</div>
                         <div className="text-muted fs-7">
                              Entité : <span className="fw-bold">{preview.entite ?? "—"}</span>
                              {" · "}
                              Statut : <span className="fw-bold">{preview.statut ?? "—"}</span>
                              {" · "}
                              Email personnel : <span className="fw-bold">{preview.mailPersonnel ?? "—"}</span>
                         </div>
                         <div className="text-muted fs-8 mt-1">Les documents dépendent de l'entité de rattachement et du statut.</div>
                    </div>
                    {dejaEnvoye && <span className="badge badge-light-success align-self-center"><i className="fas fa-paper-plane me-1" /> Déjà envoyés</span>}
               </div>

               {noMail && (
                    <div className="alert alert-warning">Le collaborateur n'a pas d'adresse email personnelle renseignée : impossible d'envoyer les documents.</div>
               )}

               {nbDocs > 0 ? (
                    <>
                         <div className="d-flex flex-column gap-2 mb-4">
                              {preview.documentsEntite.map((doc, i) => (
                                   <div
                                        key={i}
                                        className="d-flex align-items-center gap-3 border border-dashed border-gray-300 rounded px-4 py-3 cursor-pointer bg-hover-light-primary"
                                        onClick={() => setActiveDoc(doc)}
                                        title="Cliquez pour visualiser"
                                   >
                                        <i className="fas fa-file-pdf fs-3 text-danger" />
                                        <span className="text-gray-800 fw-semibold text-truncate">{doc.name ?? "Document"}</span>
                                        {doc.size != null && <span className="badge badge-light ms-auto">{formatFileSize(doc.size)}</span>}
                                        <i className="fas fa-eye text-muted" />
                                   </div>
                              ))}
                         </div>
                         <div className={`d-flex align-items-center justify-content-between mb-2 p-3 rounded ${tooLarge ? "bg-light-danger" : "bg-light"}`}>
                              <span className="fw-semibold text-gray-800">Taille totale</span>
                              <span className={`fw-bold ${tooLarge ? "text-danger" : "text-gray-800"}`}>
                                   {formatFileSize(totalSize)} / {formatFileSize(maxEmailSize)}
                              </span>
                         </div>
                         {tooLarge && (
                              <div className="alert alert-danger">
                                   Les documents dépassent la taille maximale autorisée pour un envoi par e-mail ({formatFileSize(maxEmailSize)}). Utilisez plutôt le téléchargement du ZIP.
                              </div>
                         )}
                    </>
               ) : (
                    <div className="alert alert-warning">Aucun document d'embauche n'est configuré pour cette entité et ce statut.</div>
               )}

               {/* Historique des envois */}
               {suivis.length > 0 && (
                    <>
                         <div className="separator separator-dashed my-4" />
                         <div className="fw-bold text-gray-700 mb-2">Historique des envois</div>
                         {suivis.map((s, i) => (
                              <div key={i} className="d-flex align-items-start mb-2">
                                   <i className="fas fa-paper-plane text-success fs-6 me-3 mt-1" />
                                   <div>
                                        <div className="fs-7">
                                             {s.date ? moment(s.date).format("DD/MM/YYYY à HH:mm") : ""}
                                             {s.auteur ? ` — ${s.auteur}` : ""}
                                        </div>
                                        {s.commentaire && <div className="fs-8 text-muted">{s.commentaire}</div>}
                                   </div>
                              </div>
                         ))}
                    </>
               )}

               <div className="d-flex justify-content-end gap-2 mt-4">
                    <button
                         type="button"
                         className="btn btn-sm btn-light-primary d-inline-flex align-items-center"
                         onClick={handleTelecharger}
                         disabled={downloading || nbDocs === 0}
                    >
                         {downloading ? <MySimpleSpinner size={"sm"} /> : <><i className="fas fa-download me-1" /> Télécharger le ZIP</>}
                    </button>
                    <button
                         type="button"
                         className="btn btn-sm btn-success d-inline-flex align-items-center"
                         onClick={handleEnvoyer}
                         disabled={submitting || noMail || nbDocs === 0 || tooLarge}
                         title={tooLarge ? "Taille trop importante pour un envoi par e-mail" : undefined}
                    >
                         {submitting ? (
                              <MySimpleSpinner size={"sm"} />
                         ) : (
                              <>
                                   {dejaEnvoye ? "Renvoyer les documents" : "Envoyer les documents"} <i className="fas fa-paper-plane ms-1" />
                              </>
                         )}
                    </button>
               </div>

               {activeDoc && (
                    <MyGoogleDriveFileViewerInModal
                         googleDriveID={activeDoc.googleDriveID}
                         googleDriveMimetype={activeDoc.mimeType ?? ""}
                         modalTitle={<>{activeDoc.name ?? "Document"}</>}
                         handleCloseModal={() => setActiveDoc(null)}
                    />
               )}
          </div>
     )
}

export default PersonnelDocumentsEmbauche
